/**
 * Playbooks API hooks — CRUD + arm/disarm + alarm attachment.
 * Playbooks are project-scoped. Execution is async on the BE (not request-level).
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { useToast } from '@vnetwork/ui/feedback';
import {
  healthcheckApi,
  hcKeys,
  hcExtractError,
  unwrap,
  unwrapList,
  type Playbook,
  type PlaybookAction,
} from './_shared';

// ---- Input types -----------------------------------------------------------

export interface CreatePlaybookInput {
  project_id: string;
  name: string;
  actions: PlaybookAction[];
  /** Always sent as false — arm explicitly via useArmPlaybook. */
  is_active?: false;
}

export interface UpdatePlaybookInput {
  name?: string;
  actions?: PlaybookAction[];
  is_active?: boolean;
}

// ---- Queries ---------------------------------------------------------------

export function usePlaybooks(projectId: string | undefined) {
  return useQuery<Playbook[]>({
    queryKey: hcKeys.playbooks(projectId ? { project_id: projectId } : {}),
    enabled: !!projectId,
    staleTime: 15_000,
    queryFn: async () =>
      unwrapList<Playbook>(
        await healthcheckApi.get('/playbooks', { params: { project_id: projectId } }),
      ).items,
  });
}

export function usePlaybook(id: string | undefined) {
  return useQuery<Playbook>({
    queryKey: hcKeys.playbook(id ?? ''),
    enabled: !!id,
    staleTime: 15_000,
    queryFn: async () => unwrap<Playbook>(await healthcheckApi.get(`/playbooks/${id}`)),
  });
}

// ---- Mutations -------------------------------------------------------------

export function useCreatePlaybook(
  options?: UseMutationOptions<Playbook, Error, CreatePlaybookInput>,
) {
  const qc = useQueryClient();
  const { show } = useToast();
  return useMutation<Playbook, Error, CreatePlaybookInput>({
    mutationFn: async (input) =>
      unwrap<Playbook>(
        await healthcheckApi.post('/playbooks', { ...input, is_active: false }),
      ),
    ...options,
    onSuccess: (...args) => {
      const pb = args[0];
      qc.invalidateQueries({ queryKey: hcKeys.playbooks({ project_id: pb.project_id }) });
      show({ tone: 'success', title: `Playbook "${pb.name}" created (disarmed)` });
      options?.onSuccess?.(...args);
    },
    onError: (e, ...rest) => {
      show({ tone: 'danger', title: hcExtractError(e, 'Create failed') });
      options?.onError?.(e, ...rest);
    },
  });
}

export function useUpdatePlaybook(
  id: string,
  options?: UseMutationOptions<Playbook, Error, UpdatePlaybookInput>,
) {
  const qc = useQueryClient();
  const { show } = useToast();
  return useMutation<Playbook, Error, UpdatePlaybookInput>({
    mutationFn: async (input) =>
      unwrap<Playbook>(await healthcheckApi.put(`/playbooks/${id}`, input)),
    ...options,
    onSuccess: (...args) => {
      const pb = args[0];
      qc.invalidateQueries({ queryKey: hcKeys.playbook(id) });
      qc.invalidateQueries({ queryKey: hcKeys.playbooks({ project_id: pb.project_id }) });
      show({ tone: 'success', title: 'Playbook updated' });
      options?.onSuccess?.(...args);
    },
    onError: (e, ...rest) => {
      show({ tone: 'danger', title: hcExtractError(e, 'Update failed') });
      options?.onError?.(e, ...rest);
    },
  });
}

export function useDeletePlaybook(options?: UseMutationOptions<void, Error, string>) {
  const qc = useQueryClient();
  const { show } = useToast();
  return useMutation<void, Error, string>({
    mutationFn: async (id) => {
      await healthcheckApi.delete(`/playbooks/${id}`);
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: ['hc', 'playbooks'] });
      show({ tone: 'success', title: 'Playbook deleted' });
      options?.onSuccess?.(...args);
    },
    onError: (e, ...rest) => {
      show({ tone: 'danger', title: hcExtractError(e, 'Delete failed') });
      options?.onError?.(e, ...rest);
    },
  });
}

/**
 * Arm or disarm a playbook (PUT {is_active}).
 * Arming should always be gated behind a ConfirmDialog at the call-site.
 */
export function useArmPlaybook(
  id: string,
  options?: UseMutationOptions<Playbook, Error, boolean>,
) {
  const qc = useQueryClient();
  const { show } = useToast();
  return useMutation<Playbook, Error, boolean>({
    mutationFn: async (is_active) =>
      unwrap<Playbook>(await healthcheckApi.put(`/playbooks/${id}`, { is_active })),
    ...options,
    onSuccess: (...args) => {
      const pb = args[0];
      qc.invalidateQueries({ queryKey: hcKeys.playbook(id) });
      qc.invalidateQueries({ queryKey: hcKeys.playbooks({ project_id: pb.project_id }) });
      show({
        tone: pb.is_active ? 'success' : 'info',
        title: pb.is_active ? `"${pb.name}" armed` : `"${pb.name}" disarmed`,
      });
      options?.onSuccess?.(...args);
    },
    onError: (e, ...rest) => {
      show({ tone: 'danger', title: hcExtractError(e, 'Update failed') });
      options?.onError?.(e, ...rest);
    },
  });
}

export type { Playbook, PlaybookAction };
